<script setup lang="ts">
import { ref } from 'vue';
import VueEasyLightbox from 'vue-easy-lightbox';
import { Picture } from '~/types';
import Image from '~/components/ui/Image.vue';
import Container from '~/components/Container.vue';
import { A11y } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/vue';
import { Swiper as SwiperClass, SwiperModule } from 'swiper/types';
import { Icon } from '@iconify/vue';

const visibleRef = ref(false);

const indexRef = ref<number | undefined>(0);

const onHide = () => (visibleRef.value = false);

function onShow(index: number): void {
  visibleRef.value = true;
  indexRef.value = index;
}

interface Props {
  anchor?: string;
  title?: string;
  description?: string;
  images: Picture[];
}

defineProps<Props>();

const modules: SwiperModule[] = [A11y];
const swiperUse = ref<SwiperClass | null>(null);

function onSwiper(swiper: SwiperClass) {
  swiperUse.value = swiper;
}

const swiperOptions = {
  init: false,
  wrapperTag: 'div',
  modules,
  slidesPerView: 'auto',
  keyboard: {
    enabled: true,
    onlyInViewport: false,
  },
};
</script>

<template>
  <section v-if="images && images.length > 0" :id="anchor" class="gallery-slider">
    <Container v-if="title || description" class="gallery-slider__heading">
      <h2 v-if="title" class="gallery-slider__title">
        {{ title }}
      </h2>
      <p v-if="description" class="gallery-slider__description">
        {{ description }}
      </p>
    </Container>
    <Container is-fluid>
      <teleport to="body">
        <VueEasyLightbox :visible="visibleRef" :imgs="images" :index="indexRef" @hide="onHide" />
      </teleport>
      <Swiper v-bind="swiperOptions" @swiper="onSwiper">
        <SwiperSlide v-for="(image, index) in images" :key="`tiles-slider-item-${index}`">
          <a href="#" role="button" class="gallery-slider__image-wrapper" @click.prevent="onShow(index)">
            <Image :image="image" class="gallery-slider__image" />
          </a>
        </SwiperSlide>
      </Swiper>
      <div class="gallery-slider__buttons">
        <button
          class="swiper-button-prev"
          :class="{
            'swiper-button-disabled': swiperUse?.isBeginning,
            'swiper-button-lock': swiperUse?.isLocked,
          }"
          @click="swiperUse?.slidePrev()"
        >
          <span class="sr-only">Prev Slide</span>
          <Icon icon="bi:arrow-left" />
        </button>
        <button
          class="swiper-button-next"
          :class="{
            'swiper-button-disabled': swiperUse?.isEnd,
            'swiper-button-lock': swiperUse?.isLocked,
          }"
          @click="swiperUse?.slideNext()"
        >
          <span class="sr-only">Next Slide</span>
          <Icon icon="bi:arrow-right" />
        </button>
      </div>
    </Container>
  </section>
</template>

<style lang="scss" scoped>
.gallery-slider {
  @apply my-10 overflow-hidden;
  @apply md:my-16;
  @apply xl:my-24;

  &__heading {
    @apply flex flex-col items-center text-center mb-6 gap-6;
    @apply xl:mb-12 xl:gap-10;
  }

  &__title {
    @apply font-semibold text-[24px] leading-[1.1];
    @apply xl:text-[38px];
  }

  &__description {
    @apply font-medium leading-[1.3] text-[16px] text-base-color/80;
    @apply xl:text-[20px] xl:max-w-[60%] xl:mx-auto;
  }

  &__image-wrapper {
    @apply w-full block relative rounded-10 overflow-hidden aspect-[4/3];
  }

  &__image {
    @apply absolute w-full h-full inset-0 object-cover object-center duration-300 hover:scale-105;
  }

  &__buttons {
    @apply flex items-center justify-center;
  }

  .swiper {
    @apply w-[calc(100%+1.5rem)] -ml-3 overflow-visible;

    &-button-prev,
    &-button-next {
      @apply static text-accent after:hidden translate-y-0 mt-10 mx-1 h-auto w-auto p-2 [&>svg]:w-10 duration-300 hover:text-dark;
    }
  }

  :deep(.swiper-slide) {
    @apply w-4/5 px-3;
    @apply md:w-1/2;
    @apply lg:w-1/3;
    @apply 2xl:w-[calc(100%/3.5)];
  }
}
</style>
